import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface MedicalRecord {
  id: number;
  patient_id: number;
  visit_id: number;
  doctor_id: number;
  chief_complaint: string;
  vital_signs?: any;
  physical_examination?: string;
  diagnoses: Array<{
    code: string;
    name: string;
  }>;
  treatment_plan?: string;
  notes?: string;
  examination_datetime: string;
  doctor?: {
    id: number;
    name: string;
  };
}

export interface NursingAssessment {
  id: number;
  patient_id: number;
  visit_id: number;
  nurse_id: number;
  systolic_bp?: number;
  diastolic_bp?: number;
  temperature?: number;
  pulse_rate?: number;
  respiratory_rate?: number;
  height?: number;
  weight?: number;
  bmi?: number;
  bmi_category?: string;
  complaints?: string;
  nursing_notes?: string;
  assessment_datetime: string;
  vitals_summary?: any;
  nurse?: {
    id: number;
    name: string;
  };
}

export interface Prescription {
  id: number;
  prescription_number: string;
  patient_id: number;
  visit_id: number;
  doctor_id: number;
  diagnosis?: string;
  status: 'pending' | 'dispensed' | 'partially_dispensed' | 'cancelled';
  prescribed_at: string;
  dispensed_at?: string;
  total_amount: number;
  prescription_items: PrescriptionItem[];
  doctor?: {
    id: number;
    name: string;
  };
}

export interface PrescriptionItem {
  id: number;
  medication_id: number;
  quantity: number;
  dosage: string;
  frequency: string;
  duration: string;
  instructions?: string;
  unit_price: number;
  total_price: number;
  dispensed_quantity: number;
  status: string;
  medication?: {
    id: number;
    medication_name: string;
    generic_name?: string;
    dosage_form: string;
    strength: string;
  };
}

export interface Medication {
  id: number;
  medication_name: string;
  generic_name?: string;
  brand_name?: string;
  medication_code: string;
  dosage_form: string;
  strength: string;
  unit: string;
  unit_price: number;
  stock_quantity: number;
  minimum_stock: number;
  is_active: boolean;
}

export interface LabRequest {
  id: number;
  request_number: string;
  patient_id: number;
  visit_id: number;
  requesting_doctor_id?: number;
  clinical_information?: string;
  status: 'requested' | 'specimen_collected' | 'processing' | 'completed' | 'cancelled';
  requested_at: string;
  completed_at?: string;
  test_results?: LabResult[];
}

export interface LabResult {
  id: number;
  lab_request_id: number;
  test_name: string;
  result_value?: string;
  result_unit?: string;
  reference_range?: string;
  abnormal_flag: 'normal' | 'high' | 'low' | 'critical';
  comments?: string;
  result_datetime: string;
}

interface MedicalState {
  // Nursing Assessment
  nursingPatients: any[];
  currentAssessment: NursingAssessment | null;
  assessmentHistory: NursingAssessment[];
  isAssessmentLoading: boolean;

  // Doctor Examination
  doctorPatients: any[];
  currentExamination: MedicalRecord | null;
  examinationHistory: MedicalRecord[];
  isDoctorLoading: boolean;

  // Prescriptions
  prescriptions: Prescription[];
  currentPrescription: Prescription | null;
  medications: Medication[];
  isPrescriptionLoading: boolean;

  // Laboratory
  labRequests: LabRequest[];
  labResults: LabResult[];
  pendingLabWork: LabRequest[];
  isLabLoading: boolean;

  // Common
  error: string | null;
}

const initialState: MedicalState = {
  // Nursing Assessment
  nursingPatients: [],
  currentAssessment: null,
  assessmentHistory: [],
  isAssessmentLoading: false,

  // Doctor Examination
  doctorPatients: [],
  currentExamination: null,
  examinationHistory: [],
  isDoctorLoading: false,

  // Prescriptions
  prescriptions: [],
  currentPrescription: null,
  medications: [],
  isPrescriptionLoading: false,

  // Laboratory
  labRequests: [],
  labResults: [],
  pendingLabWork: [],
  isLabLoading: false,

  // Common
  error: null,
};

const medicalSlice = createSlice({
  name: 'medical',
  initialState,
  reducers: {
    // Loading states
    setAssessmentLoading: (state, action: PayloadAction<boolean>) => {
      state.isAssessmentLoading = action.payload;
    },
    setDoctorLoading: (state, action: PayloadAction<boolean>) => {
      state.isDoctorLoading = action.payload;
    },
    setPrescriptionLoading: (state, action: PayloadAction<boolean>) => {
      state.isPrescriptionLoading = action.payload;
    },
    setLabLoading: (state, action: PayloadAction<boolean>) => {
      state.isLabLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    // Nursing Assessment
    setNursingPatients: (state, action: PayloadAction<any[]>) => {
      state.nursingPatients = action.payload;
      state.isAssessmentLoading = false;
    },
    setCurrentAssessment: (state, action: PayloadAction<NursingAssessment | null>) => {
      state.currentAssessment = action.payload;
    },
    addAssessment: (state, action: PayloadAction<NursingAssessment>) => {
      state.assessmentHistory.unshift(action.payload);
      state.currentAssessment = action.payload;
    },
    updateAssessment: (state, action: PayloadAction<NursingAssessment>) => {
      const index = state.assessmentHistory.findIndex(a => a.id === action.payload.id);
      if (index !== -1) {
        state.assessmentHistory[index] = action.payload;
      }
      if (state.currentAssessment?.id === action.payload.id) {
        state.currentAssessment = action.payload;
      }
    },
    setAssessmentHistory: (state, action: PayloadAction<NursingAssessment[]>) => {
      state.assessmentHistory = action.payload;
    },

    // Doctor Examination
    setDoctorPatients: (state, action: PayloadAction<any[]>) => {
      state.doctorPatients = action.payload;
      state.isDoctorLoading = false;
    },
    setCurrentExamination: (state, action: PayloadAction<MedicalRecord | null>) => {
      state.currentExamination = action.payload;
    },
    addExamination: (state, action: PayloadAction<MedicalRecord>) => {
      state.examinationHistory.unshift(action.payload);
      state.currentExamination = action.payload;
    },
    updateExamination: (state, action: PayloadAction<MedicalRecord>) => {
      const index = state.examinationHistory.findIndex(e => e.id === action.payload.id);
      if (index !== -1) {
        state.examinationHistory[index] = action.payload;
      }
      if (state.currentExamination?.id === action.payload.id) {
        state.currentExamination = action.payload;
      }
    },
    setExaminationHistory: (state, action: PayloadAction<MedicalRecord[]>) => {
      state.examinationHistory = action.payload;
    },

    // Prescriptions
    setPrescriptions: (state, action: PayloadAction<Prescription[]>) => {
      state.prescriptions = action.payload;
      state.isPrescriptionLoading = false;
    },
    setCurrentPrescription: (state, action: PayloadAction<Prescription | null>) => {
      state.currentPrescription = action.payload;
    },
    addPrescription: (state, action: PayloadAction<Prescription>) => {
      state.prescriptions.unshift(action.payload);
      state.currentPrescription = action.payload;
    },
    updatePrescription: (state, action: PayloadAction<Prescription>) => {
      const index = state.prescriptions.findIndex(p => p.id === action.payload.id);
      if (index !== -1) {
        state.prescriptions[index] = action.payload;
      }
      if (state.currentPrescription?.id === action.payload.id) {
        state.currentPrescription = action.payload;
      }
    },
    setMedications: (state, action: PayloadAction<Medication[]>) => {
      state.medications = action.payload;
    },

    // Laboratory
    setLabRequests: (state, action: PayloadAction<LabRequest[]>) => {
      state.labRequests = action.payload;
      state.isLabLoading = false;
    },
    addLabRequest: (state, action: PayloadAction<LabRequest>) => {
      state.labRequests.unshift(action.payload);
    },
    updateLabRequest: (state, action: PayloadAction<LabRequest>) => {
      const index = state.labRequests.findIndex(r => r.id === action.payload.id);
      if (index !== -1) {
        state.labRequests[index] = action.payload;
      }
    },
    setLabResults: (state, action: PayloadAction<LabResult[]>) => {
      state.labResults = action.payload;
    },
    addLabResult: (state, action: PayloadAction<LabResult>) => {
      state.labResults.unshift(action.payload);
    },
    setPendingLabWork: (state, action: PayloadAction<LabRequest[]>) => {
      state.pendingLabWork = action.payload;
    },

    // Clear data
    clearNursingData: (state) => {
      state.nursingPatients = [];
      state.currentAssessment = null;
      state.assessmentHistory = [];
    },
    clearDoctorData: (state) => {
      state.doctorPatients = [];
      state.currentExamination = null;
      state.examinationHistory = [];
    },
    clearPrescriptionData: (state) => {
      state.prescriptions = [];
      state.currentPrescription = null;
    },
    clearLabData: (state) => {
      state.labRequests = [];
      state.labResults = [];
      state.pendingLabWork = [];
    },
    clearError: (state) => {
      state.error = null;
    },
  },
});

export const {
  setAssessmentLoading,
  setDoctorLoading,
  setPrescriptionLoading,
  setLabLoading,
  setError,
  setNursingPatients,
  setCurrentAssessment,
  addAssessment,
  updateAssessment,
  setAssessmentHistory,
  setDoctorPatients,
  setCurrentExamination,
  addExamination,
  updateExamination,
  setExaminationHistory,
  setPrescriptions,
  setCurrentPrescription,
  addPrescription,
  updatePrescription,
  setMedications,
  setLabRequests,
  addLabRequest,
  updateLabRequest,
  setLabResults,
  addLabResult,
  setPendingLabWork,
  clearNursingData,
  clearDoctorData,
  clearPrescriptionData,
  clearLabData,
  clearError,
} = medicalSlice.actions;

// Selectors
export const selectNursingPatients = (state: { medical: MedicalState }) => state.medical.nursingPatients;
export const selectCurrentAssessment = (state: { medical: MedicalState }) => state.medical.currentAssessment;
export const selectAssessmentHistory = (state: { medical: MedicalState }) => state.medical.assessmentHistory;
export const selectAssessmentLoading = (state: { medical: MedicalState }) => state.medical.isAssessmentLoading;

export const selectDoctorPatients = (state: { medical: MedicalState }) => state.medical.doctorPatients;
export const selectCurrentExamination = (state: { medical: MedicalState }) => state.medical.currentExamination;
export const selectExaminationHistory = (state: { medical: MedicalState }) => state.medical.examinationHistory;
export const selectDoctorLoading = (state: { medical: MedicalState }) => state.medical.isDoctorLoading;

export const selectPrescriptions = (state: { medical: MedicalState }) => state.medical.prescriptions;
export const selectCurrentPrescription = (state: { medical: MedicalState }) => state.medical.currentPrescription;
export const selectMedications = (state: { medical: MedicalState }) => state.medical.medications;
export const selectPrescriptionLoading = (state: { medical: MedicalState }) => state.medical.isPrescriptionLoading;

export const selectLabRequests = (state: { medical: MedicalState }) => state.medical.labRequests;
export const selectLabResults = (state: { medical: MedicalState }) => state.medical.labResults;
export const selectPendingLabWork = (state: { medical: MedicalState }) => state.medical.pendingLabWork;
export const selectLabLoading = (state: { medical: MedicalState }) => state.medical.isLabLoading;

export const selectMedicalError = (state: { medical: MedicalState }) => state.medical.error;

export default medicalSlice.reducer;